import { useEffect, useState } from 'react';
import type { RecordOutcome } from '../../app/matchRecord';
import { matchStateLabel } from '../../domain/file';
import { MATCH_STATE } from '../../domain/machine';
import { matchPurposeLabel } from '../../domain/homeB';
import { SET_PIECE_LABEL } from '../../domain/setPiece';
import { ARTIFACT_PHASE } from '../../types';
import type { CoachCardContext, CoachCardRow } from '../../types';
import { CoachCardView } from '../card/CoachCardView';
import { AdjustmentsWorkflowB } from '../capture/AdjustmentsWorkflowB';
import { FlagPlaybackB } from '../capture/FlagPlaybackB';
import { FlagsReviewSurfaceB } from '../capture/FlagsReviewSurfaceB';
import { PhaseIcon } from '../PhaseIcon';
import { phaseLabelB, statusLabelB } from '../phases';
import { BoundaryPaneB } from './BoundaryPaneB';
import {
  canAdjustClockB,
  canCompleteB,
  canTriageFlagsB,
  completionBlockersB,
  fullTimeDoorB,
  observedSplitB,
} from './recordB';
import { useMatchRecord } from './useMatchRecord';

/**
 * B spec §2.4 — **the match record** (build unit B-18; scenarios 30, 31, 33, and F-1's completion).
 *
 * The screen the `FullTime` whistle lands on, the screen the Needs-finishing queue taps into, and
 * the read-only screen an archived match keeps forever. Everything a finished match holds is here
 * — outcomes, flags and their voice notes, notes, set pieces, annotations, cards — and so are the
 * three things still doable to it: build the full-time card, adjust the clock, complete the match.
 *
 * ── The full-time door (scenario 31) ────────────────────────────────────────
 *
 * B-14's break hub opens `AdjustmentsWorkflowB` at `halftime`; this screen opens the **same
 * component** at `fullTime`, for both purposes. That is the whole of the door: a second workflow
 * for the second moment would be `new-path-parity-single-source-the-effect` in its purest form —
 * two implementations of one flow, free to drift, with the card's framing the only thing that was
 * ever supposed to differ. What the record supplies is the context and the name
 * (`fullTimeDoorB`), and the name comes from `framingForB`, so the button and the card it produces
 * cannot disagree.
 *
 * A **draft** card's row carries the same door at its own context — which is how F-1's blocker
 * checklist offers a one-tap resolution rather than an instruction.
 *
 * ── One surface at a time ───────────────────────────────────────────────────
 *
 * The workflow, the flags queue and the clock pane each REPLACE the record body rather than
 * covering it. None of them is a dialog: the record has no whistle to protect, but the same
 * argument holds for the clock pane specifically (SES-8's preview is a claim about the list behind
 * it), and a consistent "a sub-surface is the body" rule is one mental model instead of three.
 * Each returns by re-reading, because every one of them can change what the record says.
 */

export interface MatchRecordScreenProps {
  readonly matchId: string;
  /** Back to whatever pushed the record — the Matches home, the opponent file, or the takeover. */
  readonly onClose: () => void;
}

/** Which sub-surface owns the body. `record` is the screen itself. */
type Pane =
  | { readonly kind: 'record' }
  | { readonly kind: 'workflow'; readonly context: CoachCardContext }
  | { readonly kind: 'flags' }
  | { readonly kind: 'clock' };

export function MatchRecordScreen({ matchId, onClose }: MatchRecordScreenProps) {
  const { state, actions } = useMatchRecord(matchId);
  const [pane, setPane] = useState<Pane>({ kind: 'record' });
  const [showUnseen, setShowUnseen] = useState(false);

  /**
   * Land at the top of whatever is being shown.
   *
   * Navigation in B is view state in one document (§2.2 — no router, no history), so the window's
   * scroll offset survives every surface swap. The record is where that first bites: the full-time
   * whistle arrives from a break hub the user had scrolled, and the record then opens ~400 px down
   * its own page, past its header, its cards and the door — measured at 423 px on this unit's
   * browser pass. The same applies to each sub-pane, which is why `pane.kind` is in the deps.
   */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [matchId, pane.kind]);

  if (state.phase === 'loading') {
    return (
      <div className="rec" data-testid="rec" data-phase="loading">
        <p className="rec-empty">Reading the match…</p>
      </div>
    );
  }

  if (state.phase === 'failed' || state.view === null) {
    return (
      <div className="rec" data-testid="rec" data-phase="failed">
        <p className="rec-empty" role="alert" data-testid="rec-failed">
          This match could not be read. The log has the detail.
        </p>
        <button type="button" className="rec-secondary" data-testid="rec-back" onClick={onClose}>
          Back
        </button>
      </div>
    );
  }

  const view = state.view;
  const match = view.match;
  const blockers = completionBlockersB(view.unresolvedFlagCount, view.cards);
  const door = fullTimeDoorB(match.purpose, match.state);
  const split = observedSplitB(view.outcomes);
  const canComplete = canCompleteB(match.state, view.unresolvedFlagCount, view.cards);
  const completed = match.state === MATCH_STATE.archived;

  /** Every sub-surface returns the same way: close it, and re-read what it may have changed. */
  const backToRecord = (): void => {
    setPane({ kind: 'record' });
    actions.refresh();
  };

  return (
    <div
      className="rec"
      data-testid="rec"
      data-phase="ready"
      data-view={pane.kind}
      data-state={match.state}
      data-purpose={match.purpose ?? 'unset'}
      data-read={String(state.reads)}
      data-blockers={String(blockers.length)}
      data-cards={String(view.cards.length)}
      data-cancomplete={canComplete ? 'true' : 'false'}
    >
      <header className="rec__top">
        <div className="rec__topRow">
          {/* While a sub-pane owns the body it carries its own way out, and two adjacent controls
              both labelled Back — one returning to the record, one leaving it — is a choice the
              user cannot read (found reading this unit's own screenshots). */}
          {pane.kind === 'record' && (
            <button type="button" className="rec-secondary" data-testid="rec-back" onClick={onClose}>
              Back
            </button>
          )}
          <h1 className="rec__title" data-testid="rec-opponent">
            {view.opponent}
          </h1>
        </div>
        <p className="rec__meta">
          <span data-testid="rec-date">{match.date}</span>
          {match.format !== null && <span className="rec-chip">{match.format}</span>}
          {matchPurposeLabel(match.purpose) !== null && (
            <span className="rec-chip">{matchPurposeLabel(match.purpose)}</span>
          )}
          <span className="rec-chip rec-chip--state" data-testid="rec-state">
            {matchStateLabel(match)}
          </span>
          <span className="rec__minute" data-testid="rec-final-minute">
            {view.finalMinute}′
          </span>
        </p>
      </header>

      {state.notice !== null && (
        <p className="rec-notice" role="alert" data-testid="rec-notice">
          {state.notice}
          <button
            type="button"
            className="rec-linkish"
            data-testid="rec-notice-dismiss"
            onClick={actions.dismissNotice}
          >
            Dismiss
          </button>
        </p>
      )}

      {pane.kind === 'workflow' && (
        <AdjustmentsWorkflowB match={match} context={pane.context} onClose={backToRecord} />
      )}

      {pane.kind === 'flags' && <FlagsReviewSurfaceB match={match} onClose={backToRecord} />}

      {pane.kind === 'clock' && (
        <BoundaryPaneB
          matchId={matchId}
          onCommitted={backToRecord}
          onClose={() => {
            setPane({ kind: 'record' });
          }}
        />
      )}

      {pane.kind === 'record' && (
        <>
          {/* ── What the match recorded, in one line ─────────────────────── */}
          <p className="rec-summary" data-testid="rec-summary">
            {view.incrementCount} recorded {view.incrementCount === 1 ? 'instance' : 'instances'} ·{' '}
            {split.observed.length} of {view.outcomes.length} patterns seen · {view.flags.length}{' '}
            {view.flags.length === 1 ? 'flag' : 'flags'} · {view.notes.length}{' '}
            {view.notes.length === 1 ? 'note' : 'notes'} · {view.setPieces.length} set{' '}
            {view.setPieces.length === 1 ? 'piece' : 'pieces'}
          </p>

          {/* ── Cards (scenarios 29–30) ──────────────────────────────────── */}
          <section className="rec-section" data-testid="rec-cards" data-count={String(view.cards.length)}>
            <h2 className="rec-section__title">Cards</h2>
            {view.cards.length === 0 ? (
              <p className="rec-note" data-testid="rec-no-cards">
                No card was made for this match. Cards are optional — the match record is complete
                without one.
              </p>
            ) : (
              view.cards.map((card) => (
                <div className="rec-card" key={card.id} data-testid={`rec-card-${card.context}`}>
                  <CoachCardView
                    card={card}
                    purpose={match.purpose}
                    sources={view.cardSources}
                    roster={state.roster}
                    meta={match.date}
                    testId={`reccard-${card.id}`}
                  />
                  {card.status === 'draft' && (
                    <div className="rec-draft" data-testid={`rec-draft-${card.context}`}>
                      <p className="rec-note">
                        Still a draft — nobody sees it until you make the card, and the match
                        cannot be completed while it is open.
                      </p>
                      <button
                        type="button"
                        className="rec-secondary"
                        data-testid={`rec-finish-${card.context}`}
                        onClick={() => {
                          setPane({ kind: 'workflow', context: card.context });
                        }}
                      >
                        Finish this card
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}

            {/* Scenario 31 — the full-time door, both purposes, named by its framing. */}
            {door !== null && !hasCard(view.cards, door.context) && (
              <button
                type="button"
                className="rec-primary rec-door"
                data-testid="rec-fulltime-door"
                onClick={() => {
                  setPane({ kind: 'workflow', context: door.context });
                }}
              >
                {door.label}
              </button>
            )}
          </section>

          {/* ── Outcomes (scenario 33; §2.4's confirmed-first order) ─────── */}
          <section className="rec-section" data-testid="rec-outcomes">
            <h2 className="rec-section__title">What the match found</h2>
            {split.observed.length === 0 ? (
              <p className="rec-note" data-testid="rec-nothing-observed">
                Nothing was recorded during this match.
              </p>
            ) : (
              <ul className="rec-list">
                {split.observed.map((one) => (
                  <OutcomeRow key={one.hypothesis.id} outcome={one} />
                ))}
              </ul>
            )}

            {split.unseen.length > 0 && (
              <>
                <button
                  type="button"
                  className="rec-secondary"
                  data-testid="rec-unseen-toggle"
                  aria-expanded={showUnseen}
                  onClick={() => {
                    setShowUnseen((on) => !on);
                  }}
                >
                  {showUnseen ? 'Hide' : 'Show'} the {split.unseen.length} you watched for and never
                  saw
                </button>
                {showUnseen && (
                  <ul className="rec-list rec-list--quiet" data-testid="rec-unseen">
                    {split.unseen.map((one) => (
                      <OutcomeRow key={one.hypothesis.id} outcome={one} />
                    ))}
                  </ul>
                )}
              </>
            )}
          </section>

          {/* ── Flags and their voice notes (scenario 33; DIST-1's queue) ── */}
          <section className="rec-section" data-testid="rec-flags" data-unresolved={String(view.unresolvedFlagCount)}>
            <h2 className="rec-section__title">Flags</h2>
            {view.flags.length === 0 ? (
              <p className="rec-note" data-testid="rec-no-flags">
                No moments were flagged.
              </p>
            ) : (
              <ul className="rec-list">
                {view.flags.map((flag) => (
                  <li
                    className="rec-flag"
                    key={flag.observationId}
                    data-testid={`rec-flag-${flag.observationId}`}
                    data-resolved={flag.resolved ? 'true' : 'false'}
                  >
                    <span className="rec-flag__minute">{flag.matchMinute}′</span>
                    <span className="rec-flag__state">
                      {flag.resolved ? 'Triaged' : 'Still to triage'}
                    </span>
                    {flag.audioBlobId === null ? (
                      <span className="rec-flag__silent">Silent flag</span>
                    ) : (
                      <FlagPlaybackB
                        audioBlobId={flag.audioBlobId}
                        label={`the ${String(flag.matchMinute)}′ flag`}
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
            {view.unresolvedFlagCount > 0 && canTriageFlagsB(match.state) && (
              <button
                type="button"
                className="rec-primary"
                data-testid="rec-flags-review"
                onClick={() => {
                  setPane({ kind: 'flags' });
                }}
              >
                Review flags ({view.unresolvedFlagCount})
              </button>
            )}
          </section>

          {/* ── Notes and set pieces ─────────────────────────────────────── */}
          {view.notes.length > 0 && (
            <section className="rec-section" data-testid="rec-notes">
              <h2 className="rec-section__title">Notes</h2>
              <ul className="rec-list">
                {view.notes.map((note) => (
                  <li className="rec-note-row" key={note.id} data-testid={`rec-note-${note.id}`}>
                    <span className="rec-note-row__minute">{note.matchMinute}′</span>
                    <span className="rec-note-row__text">
                      {note.text ?? 'Voice note'}
                      {note.audioBlobId !== null && note.text !== null && ' · voice note attached'}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {view.setPieces.length > 0 && (
            <section className="rec-section" data-testid="rec-setpieces">
              <h2 className="rec-section__title">Set pieces</h2>
              <ul className="rec-list">
                {view.setPieces.map((piece) => (
                  <li className="rec-note-row" key={piece.id} data-testid={`rec-setpiece-${piece.id}`}>
                    <span className="rec-note-row__minute">{piece.matchMinute}′</span>
                    <span className="rec-note-row__text">
                      {SET_PIECE_LABEL[piece.type]}
                      {piece.scheme !== null && ` · ${piece.scheme}`} · {piece.markers.length}{' '}
                      {piece.markers.length === 1 ? 'marker' : 'markers'}
                      {piece.draft && ' · unfinished'}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* ── Adjust match clock (SES-8) ───────────────────────────────── */}
          {canAdjustClockB(match.state) && (
            <section className="rec-section" data-testid="rec-clock-entry">
              <h2 className="rec-section__title">Match clock</h2>
              <p className="rec-note">
                If a whistle was tapped late, the minutes on everything above can be re-derived.
              </p>
              <button
                type="button"
                className="rec-secondary"
                data-testid="rec-adjust-clock"
                onClick={() => {
                  setPane({ kind: 'clock' });
                }}
              >
                Adjust match clock
              </button>
            </section>
          )}

          {/* ── F-1 — finishing the match ────────────────────────────────── */}
          <section className="rec-section rec-finish" data-testid="rec-finish">
            <h2 className="rec-section__title">
              {completed ? 'Match complete' : 'Finish this match'}
            </h2>
            {completed ? (
              <p className="rec-note" data-testid="rec-completed">
                This match is complete. Everything above stays exactly as it is.
              </p>
            ) : match.state !== MATCH_STATE.fullTime ? (
              <p className="rec-note" data-testid="rec-not-finishable">
                This match came from an archive and is part-way through the old distillation flow.
                It stays as it is.
              </p>
            ) : (
              <>
                <p className="rec-note">
                  Completing a match files it for good: its flags are settled and its cards can no
                  longer be edited.
                </p>
                {blockers.length > 0 && (
                  <ul className="rec-blockers" data-testid="rec-blockers">
                    {blockers.map((blocker) =>
                      blocker.kind === 'flags' ? (
                        <li className="rec-blocker" key="flags" data-testid="rec-blocker-flags">
                          <span className="rec-blocker__text">
                            {blocker.count} {blocker.count === 1 ? 'flag' : 'flags'} still to
                            triage — promote, attach or dismiss each one.
                          </span>
                          <button
                            type="button"
                            className="rec-secondary"
                            data-testid="rec-blocker-flags-go"
                            onClick={() => {
                              setPane({ kind: 'flags' });
                            }}
                          >
                            Review flags
                          </button>
                        </li>
                      ) : (
                        <li
                          className="rec-blocker"
                          key={blocker.cardId}
                          data-testid={`rec-blocker-draft-${blocker.context}`}
                        >
                          <span className="rec-blocker__text">
                            The {CONTEXT_WORD[blocker.context]} card is
                            still a draft — make it, or discard it.
                          </span>
                          <button
                            type="button"
                            className="rec-secondary"
                            data-testid={`rec-blocker-draft-go-${blocker.context}`}
                            onClick={() => {
                              setPane({ kind: 'workflow', context: blocker.context });
                            }}
                          >
                            Open the card
                          </button>
                        </li>
                      ),
                    )}
                  </ul>
                )}
                <button
                  type="button"
                  className="rec-primary"
                  data-testid="rec-complete"
                  disabled={state.busy || !canComplete}
                  onClick={actions.complete}
                >
                  Complete match
                </button>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/** Whether a card already exists for this context — the door is an *open*, never a second card. */
function hasCard(cards: readonly CoachCardRow[], context: CoachCardContext): boolean {
  return cards.some((card) => card.context === context);
}

/**
 * How the record names each break in a sentence. A `Record` over the union, spelled through
 * `ARTIFACT_PHASE` — 'fullTime' is also a MatchState, and the `ses-1` rule keeps its full strength
 * in `ui-b/` precisely because `types/` offers this named map.
 */
const CONTEXT_WORD: Record<CoachCardContext, string> = {
  // Hyphenated on both arms, matching `matchStateLabel`'s "Half time" vocabulary — and, usefully,
  // neither word is a bare `MatchState` literal, which the `ses-1` lint rule refuses here.
  [ARTIFACT_PHASE.halftime]: 'half-time',
  [ARTIFACT_PHASE.fullTime]: 'full-time',
};

// B-23 — the set-piece names come from the engine's `SET_PIECE_LABEL` (imported above). A local copy
// stood here until free kicks arrived and made it the third place the same four strings lived; a
// record that calls a capture something other than the sheet that took it is a defect nothing types.

/**
 * One pattern's outcome: its status word, its count, and the annotations written about it.
 *
 * Status is never hue-only (§2.3) — the word is here, and `data-status` carries the colour half —
 * and the annotations sit under the pattern they belong to rather than in a section of their own,
 * because *"applies to #7, the sub doesn't do this"* means nothing detached from its pattern.
 */
function OutcomeRow({ outcome }: { readonly outcome: RecordOutcome }) {
  const { hypothesis, annotations } = outcome;
  return (
    <li
      className="rec-outcome"
      data-testid={`rec-outcome-${hypothesis.id}`}
      data-status={hypothesis.status}
      data-count={String(hypothesis.cachedCount)}
    >
      <span className="rec-outcome__count">{hypothesis.cachedCount}</span>
      <span className="rec-outcome__body">
        <span className="rec-outcome__text">{hypothesis.text}</span>
        <span className="rec-outcome__meta">
          <PhaseIcon phase={hypothesis.phase} />
          {phaseLabelB(hypothesis.phase)} · {statusLabelB(hypothesis.status)}
        </span>
        {annotations.map((note) => (
          <span
            className="rec-outcome__note"
            key={note.id}
            data-testid={`rec-annotation-${note.id}`}
          >
            {note.text}
          </span>
        ))}
      </span>
    </li>
  );
}
